import type {
  FinancialPositionId,
  MoneyWorries,
  MoneyWorriesImpactOptionId,
  SelectableItem,
  YesNo,
} from '@physitrack/championhealth-types';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '../questions/checkbox/Checkbox';
import { CheckboxRadio } from '../questions/checkboxRadio/CheckboxRadio';
import { OneToFiveSlider } from '../questions/painSlider/OneToFiveSlider';
import { Radio } from '../questions/radio/Radio';
import { AssessmentPageItem } from '../types/AssessmentPageItem';

export function FinancialWellbeing({
  assessmentValues,
  questionid,
  questionHeading,
  questionText,
  onSetValue,
}: AssessmentPageItem) {
  const { t } = useTranslation();
  const finPosOptions: SelectableItem<FinancialPositionId>[] = [
    { id: 'very_comfortable', title: t('FW001') },
    { id: 'relatively_comfortable', title: t('FW002') },
    {
      id: 'cannot_afford_luxuries',
      title: t('FW003'),
    },
    {
      id: 'can_afford_essentials',
      title: t('FW004'),
    },
    {
      id: 'cannot_afford_essentials',
      title: t('FW005'),
    },
  ];

  const confidenceTextValues = [t('FW006'), t('FW007'), t('FW008'), t('FW009'), t('FW010')];

  const sometimesOptions: SelectableItem<MoneyWorries>[] = [
    { id: 'yes', title: t('FW011') },
    { id: 'sometimes', title: t('FW012') },
    { id: 'no', title: t('FW013') },
  ];

  const moneyWorriesOptions: SelectableItem<MoneyWorriesImpactOptionId>[] = [
    {
      id: 'none',
      title: t('FW014'),
    },
    { id: 'mental_health', title: t('FW015') },
    { id: 'physical_health', title: t('FW016') },
    {
      id: 'relationships',
      title: t('FW017'),
    },
    {
      id: 'ability_to_sleep',
      title: t('FW018'),
    },
    {
      id: 'work_performance',
      title: t('FW019'),
    },
    {
      id: 'job_satisfaction',
      title: t('FW020'),
    },
    {
      id: 'alcohol_consumption',
      title: t('FW021'),
    },
    {
      id: 'other',
      title: t('FW022'),
    },
  ];

  const yesnoOptions: SelectableItem<YesNo>[] = [
    { id: 'yes', title: t('FW023') },
    { id: 'no', title: t('FW024') },
  ];

  useEffect(() => {
    if (assessmentValues.money_worries === 'no') {
      onSetValue({ id: 'money_worries_impact', value: null });
      onSetValue({ id: 'able_discuss_money', value: null });
    }
  }, [assessmentValues.money_worries, onSetValue]);

  return (
    <div className="question_container" id={questionid}>
      <h1>{questionHeading}</h1>
      <p>{questionText}</p>
      <CheckboxRadio
        questionid="financial_position"
        width="_wide"
        options={finPosOptions}
        questionTitle={t('assessment.question.financial_position')}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
      />
      <fieldset data-input-type="over-last-few-months" className="question_block_container stacked">
        <legend className="introduction assessment_question_title" id="financial_position_legend">
          {t('FW027')}
        </legend>
        <OneToFiveSlider
          questionid="confident_managing_finance"
          questionTitle={t('assessment.question.confident_managing_finance')}
          width=""
          textValues={confidenceTextValues}
          onSetValue={onSetValue}
          assessmentValues={assessmentValues}
          labelId="financial_position_legend"
        />
        <OneToFiveSlider
          questionid="relies_on_borrowing"
          questionTitle={t('assessment.question.relies_on_borrowing')}
          width=""
          textValues={confidenceTextValues}
          onSetValue={onSetValue}
          assessmentValues={assessmentValues}
          labelId="financial_position_legend"
        />
        <OneToFiveSlider
          questionid="in_control_finances"
          questionTitle={t('assessment.question.in_control_finances')}
          width=""
          textValues={confidenceTextValues}
          onSetValue={onSetValue}
          assessmentValues={assessmentValues}
          labelId="financial_position_legend"
        />
        <OneToFiveSlider
          questionid="cannot_meet_financial_goals"
          questionTitle={t('assessment.question.cannot_meet_financial_goals')}
          width=""
          textValues={confidenceTextValues}
          onSetValue={onSetValue}
          assessmentValues={assessmentValues}
          labelId="financial_position_legend"
        />
      </fieldset>
      <Radio
        questionid="money_worries"
        width=""
        questionTitle={t('assessment.question.money_worries')}
        options={sometimesOptions}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
      />
      {assessmentValues.money_worries && assessmentValues.money_worries !== 'no' && (
        <>
          <Checkbox
            questionid="money_worries_impact"
            width=""
            itemLimit="1"
            questionTitle={t('assessment.question.money_worries_impact')}
            questionTitleSuffix={t('FW034')}
            options={moneyWorriesOptions}
            onSetValue={onSetValue}
            assessmentValues={assessmentValues}
            secondaryQ
            fadeIn="yes"
          />
          <Radio
            questionid="able_discuss_money"
            width=""
            questionTitle={t('assessment.question.able_discuss_money')}
            options={sometimesOptions}
            onSetValue={onSetValue}
            assessmentValues={assessmentValues}
            fadeIn="yes"
          />
        </>
      )}
      <Radio
        questionid="sought_financial_advice"
        width=""
        questionTitle={t('assessment.question.sought_financial_advice')}
        questionTitleSuffix={t('FW038')}
        options={yesnoOptions}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
      />
    </div>
  );
}
